import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Box, Button } from "@mui/material";
import Login from "./login";
import TermsAndConditions from "./TermsAndConditions";
import VerificationCode from "./VerificationCode";
import SerialNumberCheck from "./SerialNumberCheck";
import { loginRequestCaps } from "../authConfig";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import "./styles.css";
import * as constants from "../utils/constants.ts";
import useSignOut from "../components/SignOut";
import { ENVIRONMENTS, getValueByEnv } from "../utils/runtimeEnv";

const API_BASE_URL = getValueByEnv({
  [ENVIRONMENTS.LOCAL]: constants.API_ENDPOINT_DEV,
  [ENVIRONMENTS.DEV]: constants.API_ENDPOINT_DEV,
  [ENVIRONMENTS.TEST]: constants.API_ENDPOINT_TEST,
  [ENVIRONMENTS.PROD]: constants.API_ENDPOINT_PROD,
});

const errorTitleStyle = {
  paddingBottom: "10px",
  fontSize: "20px",
  color: "black",
  fontWeight: "bold",
};

const errorBodyStyle = {
  paddingBottom: "20px",
  fontSize: "14px",
  fontWeight: "normal",
  color: "black",
};

const MainPage = () => {
  const [savedOrderNumber, setSavedOrderNumber] = useState(-1);
  const [savedJobType, setSavedJobType] = useState("");
  const [savedSerialNumber, setSavedSerialNumber] = useState("");
  const [showLogin, setShowLogin] = useState(true);
  const [showSerialNumberCheck, setShowSerialNumberCheck] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [token, setToken] = useState(null);
  const [errorOccurred, setErrorOccurred] = useState(false);
  const [serialError, setSerialError] = useState(false);
  const { instance, inProgress } = useMsal();
  const [showAuthenticating, setShowAuthenticating] = useState(inProgress !== InteractionStatus.None);
  const [passcode, setPasscode] = useState("");
  const handleSignOut = useSignOut();
  const hasProcessedRedirect = useRef(false);
  const reacquireToken = () => {
    setShowAuthenticating(true);
    const request = {
      scopes: ["openid"],
      account: instance.getAllAccounts()[0],
    };

    instance
      .acquireTokenSilent(request)
      .then((tokenResponse) => {
        setToken(tokenResponse.idToken);
        setShowLogin(false);
        setShowAuthenticating(false);
        setShowTerms(true);
      })
      .catch((error) => {
        console.log("error", error);
        setShowAuthenticating(false);
      });
  };
  useEffect(() => {
    if (inProgress !== InteractionStatus.None || hasProcessedRedirect.current) {
      return;
    }

    hasProcessedRedirect.current = true;
    setShowAuthenticating(true);

    instance
      .handleRedirectPromise()
      .then((response) => {
        if (response !== null) {
          window.DD_RUM &&
            window.DD_RUM.setUser({
              id: response.account.idTokenClaims.ProfileID,
            });
          setToken(response.idToken);
          setShowLogin(false);
          setShowAuthenticating(false);
          setShowTerms(true);
        } else if (instance.getAllAccounts()[0]) {
          reacquireToken();
          window.DD_RUM &&
            window.DD_RUM.setUser({
              id: instance.getAllAccounts()[0]?.idTokenClaims?.ProfileID,
            });
        } else {
          setShowAuthenticating(false);
          setShowLogin(true);
          setShowTerms(false);
        }
      })

      .catch((error) => {
        console.log("error", error);
        setShowAuthenticating(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, inProgress]);

  const setTnCAndOrderNumber = async (TermNCond, OrderNum, serialNum,JobTy) => {
    const config = {
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };
    const data = {
      TnC: TermNCond,
      OrderNumber: OrderNum,
      SerialNumber: serialNum,
      JobType: JobTy,
    };

    try {
      const api = `${API_BASE_URL}/api/generateordernumber`;
      const response = await axios.post(api, data, config);
      console.log(
        "TnC, Job Type, and OrderNumber set successfully:",
        response.data
      );
      console.log("response.data.Passcode", response.data.Passcode);
      if (response?.data?.Passcode) {
        setPasscode(response.data.Passcode);
        setErrorOccurred(false);
        return {
          tncstatus: response.status,
          tncmessage: response.data.RespMess || "No message",
        };
      }
    } catch (error) {
      setErrorOccurred(true);
      console.log("Failed to set TnC, Job Type, and OrderNumber:", error);
      if (error.response?.data?.description === "Token has expired") {
        handleSignOut();
      }
    }
  };

  const checkSerialNumber = async (serialNumber) => {
    const endpoint = "/api/customers/tnc-acceptance";
    const config = {
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };
    const data = {
      serial_number: serialNumber,
    };

    try {
      const api = `${API_BASE_URL}${endpoint}`;
      const response = await axios.post(api, data, config);
      return {
        snstatus: response.status,
        snmessage: response.data.RespMess || "No message",
      };
    } catch (error) {
      if (error.response) {
        if (error.response?.data?.description === "Token has expired") {
          handleSignOut();
        }
        return {
          snstatus: error.response.status,
          snmessage: error.response.data.message || "No message",
        };
      } else {
        return { status: 500, message: error.message };
      }
    }
  };

  const handleSignIn = () => {
    if (inProgress !== InteractionStatus.None) {
      return;
    }
    setShowAuthenticating(true);
    instance.loginRedirect(loginRequestCaps).catch((error) => {
      if (error?.errorCode !== "interaction_in_progress") {
        console.error(error);
        setShowAuthenticating(false);
      }
    });
  };

  const handleLogin = () => {
    handleSignIn();
  };

  const handleTermsAccept = async (orderNumber, jobType, serialNumber) => {
    setShowTerms(false);
    setSavedSerialNumber(serialNumber);
    setSavedOrderNumber(orderNumber || -1);
    setSavedJobType(jobType);
    const { snstatus } = await checkSerialNumber(serialNumber);
    if (snstatus === 200) {
      const { tncstatus } = await setTnCAndOrderNumber(
        true,
        orderNumber || -1,
        serialNumber,
        jobType
      );
      if (tncstatus === 200) {
        setShowVerification(true);
        setSerialError(false);
      }
    } else if (snstatus === 400) {
      setShowSerialNumberCheck(true);
      setSerialError(false);
    } else {
      setShowTerms(true);
      setSerialError(true);
    }
  };

  const handleRetry = () => {
    setTnCAndOrderNumber(true, savedOrderNumber,savedSerialNumber, savedJobType);
  };

  const handleSerialNumberCheckAccept = async () => {
    const { snstatus } = await checkSerialNumber(savedSerialNumber);
    if (snstatus === 200) {
      setShowSerialNumberCheck(false);
      setShowVerification(true);
      setTnCAndOrderNumber(true, savedOrderNumber, savedSerialNumber, savedJobType);
    } else {
      setShowSerialNumberCheck(true);
    }
  };

  const handleVerification = () => {
    setShowVerification(false);
  };

  return (
    <Box className="main-page-box">
      {showLogin && <Login onLogin={handleLogin} isAuthenticating={showAuthenticating} />}
      {showSerialNumberCheck && (
        <SerialNumberCheck onAccept={handleSerialNumberCheckAccept} />
      )}
      {showTerms && (
        <TermsAndConditions
          onAccept={handleTermsAccept}
          serialError={serialError}
          setSerialError={setSerialError}
        />
      )}
      {showVerification && (
        <VerificationCode
          onRetry={handleRetry}
          onSuccess={handleVerification}
          passcode={passcode}
        />
      )}
      {errorOccurred && !showVerification && (
        <div className="retry">
          <div style={errorTitleStyle}>
            An error occurred. Click below to regenerate the code.
          </div>
          <div style={errorBodyStyle}>
            If you continue to get this error, please call the GM Energy
            Customer Support Center at{" "}
            <span style={{ whiteSpace: "nowrap" }}>1-833-64POWER</span>
          </div>
          <Button
            variant="contained"
            color="primary"
            size="small"
            sx={{
              fontSize: "12px",
              backgroundColor: "#000",
              "&:hover": { backgroundColor: "#0009FF" },
            }}
            onClick={handleRetry}
          >
            Regenerate Code
          </Button>
        </div>
      )}
    </Box>
  );
};

export default MainPage;
